import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Plane, PlaneTakeoff, Users, Wallet } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { useRosterSummary } from '@/lib/disruptions/use-disruptions';
import { formatPriceMinor } from './labels';

// disruption-roster-mvp-parity (epic NEX-369 / U1) — the 4 MVP header tiles for
// the disruption Passengers roster, mirroring the MVP "SCL Severe Storm" look
// (mvp EventHeader + ImpactKpiCards):
//
//   ┌ AFFECTED FLIGHTS ┬ PASSENGERS ┬ CONNECTIONS AT RISK ┬ ESTIMATED COST ┐
//   │  affectedFlights  │ passengers │  count of total      │  $ estimate    │
//   └──────────────────┴───────────┴─────────────────────┴────────────────┘
//
// Data source: `useRosterSummary(urn)` → operator-bff:GET
// /v1/disruptions/{urn}/roster-summary@v1 (B1), a read-aggregation over the
// disruption rollup + rebooking-detail group results — no client-side compute,
// no PII. Realtime = WS (no polling): the hook's key is nested under the roster
// prefix, so the existing `DisruptionProgressChanged` handler refreshes the tiles
// live over the disruption WS room on a terminal/settle push.
//
// The ESTIMATED COST tile is clickable → the caller routes to the existing
// cost-breakdown read (the Cost tab). Friendly labels primary; white-label tone
// tokens only (no hard-coded brand hex). Responsive: 2-up on phone, 4-up ≥lg,
// with the row never growing the page (frontend-conventions §18).

interface RosterMetricTilesProps {
  disruptionUrn: string;
  /** Open the existing cost-breakdown read (the Cost tab). Makes the ESTIMATED
   *  COST tile a button when provided (MVP: the cost KPI drills into the breakdown). */
  onCostClick?: (() => void) | undefined;
}

export function RosterMetricTiles({ disruptionUrn, onCostClick }: RosterMetricTilesProps) {
  const { t } = useTranslation('roster');
  const { data, isLoading, isError, refetch } = useRosterSummary(disruptionUrn);

  if (isLoading) {
    return (
      <div
        className="grid shrink-0 grid-cols-2 gap-3 lg:grid-cols-4"
        data-testid="roster-metric-tiles-loading"
        aria-busy="true"
      >
        {[0, 1, 2, 3].map((i) => (
          <Card key={i}>
            <CardContent className="flex flex-col gap-2 p-4">
              <Skeleton className="h-3 w-24" />
              <Skeleton className="h-7 w-16" />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  if (isError || !data) {
    return (
      <Card className="shrink-0" data-testid="roster-metric-tiles-error">
        <CardContent className="flex flex-wrap items-center justify-between gap-2 p-4 text-sm">
          <span className="text-muted-foreground">{t('tiles.loadError')}</span>
          <button
            type="button"
            onClick={() => void refetch()}
            className="rounded-md border border-input bg-background px-3 py-1 text-sm font-medium text-foreground hover:bg-muted"
            data-testid="roster-metric-tiles-retry"
          >
            {t('actions.retry')}
          </button>
        </CardContent>
      </Card>
    );
  }

  const { count, total } = data.connectionsAtRisk;

  return (
    <div
      className="grid shrink-0 grid-cols-2 gap-3 lg:grid-cols-4"
      data-testid="roster-metric-tiles"
      role="list"
      aria-label={t('tiles.summaryLabel')}
    >
      <Tile
        label={t('tiles.affectedFlights')}
        value={data.affectedFlights.toLocaleString()}
        icon={<Plane className="h-4 w-4" aria-hidden />}
        testid="tile-affected-flights"
      />
      <Tile
        label={t('tiles.passengers')}
        value={data.passengers.toLocaleString()}
        icon={<Users className="h-4 w-4" aria-hidden />}
        testid="tile-passengers"
      />
      <Tile
        label={t('tiles.connectionsAtRisk')}
        value={count.toLocaleString()}
        sublabel={t('tiles.connectionsOfTotal', { total: total.toLocaleString() })}
        icon={<PlaneTakeoff className="h-4 w-4" aria-hidden />}
        testid="tile-connections-at-risk"
      />
      {/* NEX-603: Clarify this is an ESTIMATE including projections. airline-money@v2:
          the airline's PRICE (converted server-side); null → «No disponible». */}
      <Tile
        label={t('tiles.estimatedPrice')}
        value={formatPriceMinor(data.estimatedPriceMinor, data.currency, t)}
        sublabel={t('tiles.estimatedPriceTooltip')}
        icon={<Wallet className="h-4 w-4" aria-hidden />}
        onClick={onCostClick}
        actionHint={t('tiles.priceHint')}
        testid="tile-estimated-cost"
      />
    </div>
  );
}

interface TileProps {
  label: string;
  value: string;
  sublabel?: string;
  icon: ReactNode;
  onClick?: (() => void) | undefined;
  actionHint?: string;
  testid: string;
}

function Tile({ label, value, sublabel, icon, onClick, actionHint, testid }: TileProps) {
  const { t } = useTranslation('roster');
  const clickable = typeof onClick === 'function';
  return (
    <Card
      role={clickable ? 'button' : 'listitem'}
      onClick={clickable ? onClick : undefined}
      onKeyDown={
        clickable
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onClick?.();
              }
            }
          : undefined
      }
      tabIndex={clickable ? 0 : undefined}
      aria-label={clickable ? actionHint : undefined}
      className={
        clickable
          ? 'cursor-pointer transition-colors hover:bg-muted/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring'
          : undefined
      }
      data-testid={testid}
    >
      <CardContent className="flex flex-col gap-1 p-4">
        <div className="flex items-start justify-between gap-2">
          <span className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
            {label}
          </span>
          <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-muted text-muted-foreground">
            {icon}
          </span>
        </div>
        <span className="text-2xl font-semibold tabular-nums text-foreground">{value}</span>
        <div className="flex min-h-[1rem] items-center gap-2 text-[11px]">
          {sublabel ? <span className="text-muted-foreground">{sublabel}</span> : null}
          {clickable ? (
            <span className="ml-auto uppercase tracking-wide text-primary">
              {t('tiles.viewBreakdown')}
            </span>
          ) : null}
        </div>
      </CardContent>
    </Card>
  );
}
